import api from './api';

// ─── Meta ─────────────────────────────────────────────────────────────────────

// Canonical enumerations (tenant_types, auth_modes) — mirrors the DB CHECK constraints so the UI
// renders dropdowns from one source instead of hard-coding the lists. See waive_core.constants.
export async function fetchAdminMeta() {
    const res = await api.get('/admin/meta');
    return res.data; // { tenant_types: [...], auth_modes: [...] }
}

// ─── Tenants ──────────────────────────────────────────────────────────────────

export async function fetchTenants(tenantType = null) {
    const params = tenantType ? { tenant_type: tenantType } : {};
    const res = await api.get('/admin/tenants', { params });
    return res.data;
}

export async function createTenant(data) {
    const res = await api.post('/admin/tenants', data);
    return res.data;
}

export async function updateTenant(id, data) {
    const res = await api.patch(`/admin/tenants/${id}`, data);
    return res.data;
}

// Permanently delete a tenant and deprovision its white-label host (ADR-0032/0050). Destructive +
// irreversible: tears down the subdomain (AFD custom domain + DNS), any legacy Keycloak realm, then the
// hub tenant row. Keyed by CODE (not id), matching the admin-api route.
export async function deleteTenant(code) {
    const res = await api.delete(`/admin/tenants/${code}`);
    return res.data; // { deleted, subdomain_removed, warnings: [...] }
}

// ─── SSO identity providers (ADR-0022) ───────────────────────────────────────

// Returns the tenant's configured OIDC IdP (secret redacted) or throws 404 if none.
export async function getTenantIdentityProvider(code) {
    const res = await api.get(`/admin/tenants/${code}/identity-provider`);
    return res.data;
}

export async function upsertTenantIdentityProvider(code, data) {
    const res = await api.put(`/admin/tenants/${code}/identity-provider`, data);
    return res.data;
}

export async function verifyTenantIdentityProvider(code) {
    const res = await api.post(`/admin/tenants/${code}/identity-provider/verify`);
    return res.data;
}

// SSO preflight (ADR-0031 / tech-debt §C): checks the tenant's SAVED issuer + JWKS are reachable and
// consistent, so a bad config fails at onboarding, not at a member's first login.
export async function verifyTenantSso(code) {
    const res = await api.post(`/admin/tenants/${code}/sso/verify`);
    return res.data; // { tenant, ok, discovery_reachable, issuer_matches, jwks_reachable, jwks_key_count, jwks_uri_matches_discovery, errors, warnings }
}

// White-label subdomain provisioning (ADR-0032). provision → validating; reconcile advances → active|failed.
export async function provisionSubdomain(code, host) {
    const res = await api.post(`/admin/tenants/${code}/subdomain`, { host });
    return res.data; // { tenant, subdomain, status, error, afd_custom_domain }
}

// Hosted-realm lifecycle (KAN-152) — master-scoped background jobs (202). provision creates the Keycloak
// realm + per-realm service account + login config; deprovision deletes the realm + its secret. Both
// idempotent; hosted tenants only (admin-api 400s a federated tenant).
export async function provisionRealm(code) {
    const res = await api.post(`/admin/tenants/${code}/realm/provision`);
    return res.data; // 202 { tenant, realm, action, status }
}

export async function deprovisionRealm(code) {
    const res = await api.delete(`/admin/tenants/${code}/realm`);
    return res.data; // 202 { tenant, realm, action, status }
}

// Employer-roster upload (KAN-155) — JSON array of members; provisions the member plane and (hosted
// tenants) the Keycloak users via the background provisioning job.
export async function uploadRoster(code, members) {
    const res = await api.post(`/admin/tenants/${code}/roster`, members);
    return res.data; // { run_id, tenant, auth_mode, members, provisioned, status }
}

export async function reconcileSubdomain(code) {
    const res = await api.post(`/admin/tenants/${code}/subdomain/reconcile`);
    return res.data;
}

export async function getSubdomainStatus(code) {
    const res = await api.get(`/admin/tenants/${code}/subdomain`);
    return res.data;
}

// ─── Tenant ↔ Provider associations (ADR-0028) ────────────────────────────────

export async function fetchTenantProviders(code) {
    const res = await api.get(`/admin/tenants/${code}/providers`);
    return res.data; // { tenant_code, providers, count }
}

export async function linkTenantProvider(code, providerCode) {
    const res = await api.post(`/admin/tenants/${code}/providers`, { provider_code: providerCode });
    return res.data;
}

export async function unlinkTenantProvider(code, providerCode) {
    const res = await api.delete(`/admin/tenants/${code}/providers/${providerCode}`);
    return res.data;
}

// ─── Providers ────────────────────────────────────────────────────────────────

export async function fetchProviders() {
    const res = await api.get('/admin/providers');
    return res.data;
}

export async function createProvider(data) {
    const res = await api.post('/admin/providers', data);
    return res.data;
}

export async function updateProvider(id, data) {
    const res = await api.patch(`/admin/providers/${id}`, data);
    return res.data;
}

export async function createFund(data) {
    const res = await api.post('/admin/funds', data);
    return res.data;
}

export async function updateFund(id, data) {
    const res = await api.patch(`/admin/funds/${id}`, data);
    return res.data;
}

export async function fetchProviderFunds(providerCode) {
    const res = await api.get(`/admin/providers/${providerCode}/funds`);
    return res.data;
}

// ─── Configuration (tenant portfolio config via existing config API) ──────────

// Reads directly from DB — use this in preference to fetchTenantConfig for admin UI.
export async function fetchTenantConfigFromDB(code) {
    const res = await api.get(`/admin/config/tenants/${code}`);
    return res.data;
}

export async function savePortfolioLevels(code, portfolioLevels) {
    const res = await api.put(`/admin/config/tenants/${code}/portfolio-levels`, { portfolio_levels: portfolioLevels });
    return res.data;
}

export async function saveFundAllocations(code, allocationMatrix) {
    const res = await api.put(`/admin/config/tenants/${code}/fund-allocations`, { allocation_matrix: allocationMatrix });
    return res.data;
}

export async function saveRiskBands(code, riskBands) {
    const res = await api.put(`/admin/config/tenants/${code}/risk-bands`, { risk_bands: riskBands });
    return res.data;
}

export async function saveAvailableFunds(code, fundIds) {
    const res = await api.put(`/admin/config/tenants/${code}/available-funds`, { fund_ids: fundIds });
    return res.data;
}

// The tenant's default fund(s) — optional, any number, a subset of available funds (KAN-174).
export async function saveDefaultFunds(code, fundIds) {
    const res = await api.put(`/admin/config/tenants/${code}/default-funds`, { fund_ids: fundIds });
    return res.data;
}

// ─── Audit log ────────────────────────────────────────────────────────────────

export async function fetchAuditLog({ tenantCode, entityType, action, actorEmail, fromDate, toDate, page, perPage } = {}) {
    const params = {};
    if (tenantCode) params.tenant_code = tenantCode;
    if (entityType) params.entity_type = entityType;
    if (action) params.action = action;
    if (actorEmail) params.actor_email = actorEmail;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (page) params.page = page;
    if (perPage) params.per_page = perPage;
    const res = await api.get('/admin/audit-log', { params });
    return res.data;
}

// ─── Platform settings ───────────────────────────────────────────────────────

export async function fetchSettings() {
    const res = await api.get('/admin/config/settings');
    return res.data;
}

export async function saveSettings(data) {
    const res = await api.post('/admin/config/settings', { data });
    return res.data;
}

export async function reloadConfig() {
    const res = await api.post('/admin/config/reload');
    return res.data;
}

// ─── Questions / Scoring methodology ─────────────────────────────────────────

// Per-tenant questionnaires (tenant_questions — ADR-0026). (The old global fetch/save helpers that
// edited the deprecated questions.json fallback were removed — they were unused, and admin-api no
// longer serves POST /admin/config/questions. The contract test caught the dead call.)
export async function fetchTenantQuestions(code) {
    const res = await api.get(`/admin/config/tenants/${code}/questions`);
    return res.data; // { code, questions }
}

export async function saveTenantQuestions(code, questions) {
    const res = await api.put(`/admin/config/tenants/${code}/questions`, { questions });
    return res.data;
}

// ─── Funds (read via admin config API) ───────────────────────────────────────

export async function fetchAllFunds() {
    const res = await api.get('/admin/config/funds');
    return res.data.funds || [];
}

// ─── Fund training data (FundRecord — ADR-0036) ──────────────────────────────
// Compliance-authored, fund-intrinsic material the model-training pipeline ingests. Stored in the
// hub keyed 1:1 to a catalogue fund; nothing tenant-specific. Endpoints return 503 until the
// waive-core pin carries the FundRecord contract (rollout in progress).

// Per-fund training-data completeness for the catalogue table (which contexts authored, etc.).
export async function fetchFundTrainingStatus() {
    const res = await api.get('/admin/config/funds/training');
    return res.data.funds || [];
}

// The stored FundRecord for one fund; caller should treat 404 as "not yet authored".
export async function fetchFundTraining(fundId) {
    const res = await api.get(`/admin/config/funds/${fundId}/training`);
    return res.data; // { fund_id, record, fund_record_version, updated_by, updated_at }
}

// Validate + upsert a fund's FundRecord (validated server-side against the waive-core contract).
export async function saveFundTraining(fundId, record) {
    const res = await api.put(`/admin/config/funds/${fundId}/training`, record);
    return res.data;
}

// Hub regs run (train → carbon-copy → version → release) + per-tenant blue/green.  
export async function fetchTrainingRuns() {
    const res = await api.get('/admin/training/runs');
    return res.data; // { runs: [...], count }
}

export async function fetchTrainingRun(runId) {
    const res = await api.get(`/admin/training/runs/${runId}`);
    return res.data; // run + rollouts[]
}

export async function startRegsRun(regsRef, notes) {
    const res = await api.post('/admin/training/regs', { regs_ref: regsRef, notes });
    return res.data;
}

export async function trainRun(runId) {
    const res = await api.post(`/admin/training/runs/${runId}/train`);
    return res.data;
}

export async function carbonCopyRun(runId, { changeType, appVersion } = {}) {
    const body = {};
    if (changeType) body.change_type = changeType;   // 'model_and_app' on a fix-app retest
    if (appVersion) body.app_version = appVersion;
    const res = await api.post(`/admin/training/runs/${runId}/carbon-copy`, body);
    return res.data; // { passed, detail, missing_refs, confidence, diff, run }
}

export async function versionRun(runId, notes) {
    const res = await api.post(`/admin/training/runs/${runId}/version`, { notes });
    return res.data;
}

export async function releaseRun(runId) {
    const res = await api.post(`/admin/training/runs/${runId}/release`);
    return res.data; // 202 — fan-out runs async; poll fetchTrainingRun for progress
}

export async function trainTenant(code) {
    const res = await api.post(`/admin/training/tenants/${code}/train`);
    return res.data;
}

export async function uploadRegs(file) {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post('/admin/training/regs/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },   // axios 1.x adds the boundary for FormData
    });
    return res.data;
}

// ─── Member plane (tech-debt §G) — read-only member + advice-history views ──────
// Proxied by admin-api → waive-member-api (the spoke member plane; data stays in-spoke).

export async function getTenantMembers(code, params = {}) {
    const res = await api.get(`/admin/tenants/${code}/members`, { params });
    return res.data; // { items:[{member_id, external_member_id, registration_email, status, created_at}], total, limit, offset }
}

export async function getMember(code, memberId) {
    const res = await api.get(`/admin/tenants/${code}/members/${memberId}`);
    return res.data; // { ...member, client_profile, identities:[{issuer, idp_sub, linked_at}] }
}

export async function getMemberAssessments(code, memberId, params = {}) {
    const res = await api.get(`/admin/tenants/${code}/members/${memberId}/assessments`, { params });
    return res.data; // { items:[assessment summary], total, limit, offset }
}

export async function getTenantAssessments(code, params = {}) {
    const res = await api.get(`/admin/tenants/${code}/assessments`, { params });
    return res.data; // tenant-wide advice sweep
}
